import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { BAR_COUNT, FFT_SIZE } from "@/constants/conversionShowcase";
import { generateFrequencyBands } from "@/utils/generateFrequencyBands";

const frequencyBands = generateFrequencyBands(BAR_COUNT);

// Builds a Web Audio graph from the output <audio> element and maps FFT bins into BAR_COUNT display bars. The graph is created lazily on first play because AudioContext must be resumed after a user gesture on many browsers.
export const useWaveformAnalyser = (
  audioRef: RefObject<HTMLAudioElement | null>,
  isPlaying: boolean
) => {
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const frameRef = useRef<number | null>(null);
  const graphRef = useRef<{
    ctx: AudioContext;
    analyser: AnalyserNode;
    source: MediaElementAudioSourceNode;
  } | null>(null);
  // Smooths bar-to-bar motion so the UI doesn’t flicker on frame boundaries.
  const prevBarsRef = useRef<number[]>(
    Array.from({ length: BAR_COUNT }, () => 0)
  );

  const ensureAudioGraph = useCallback(async () => {
    if (graphRef.current !== null) {
      return;
    }
    const audio = audioRef.current;
    if (audio === null) {
      return;
    }

    try {
      const ctx = new AudioContext();
      if (ctx.state === "suspended") {
        await ctx.resume();
      }
      const analyser = ctx.createAnalyser();
      analyser.fftSize = FFT_SIZE;
      analyser.smoothingTimeConstant = 0.3;
      analyser.minDecibels = -100;
      analyser.maxDecibels = -10;
      const source = ctx.createMediaElementSource(audio);
      source.connect(analyser);
      // Must hear the element: analyser + destination, otherwise the graph is silent.
      analyser.connect(ctx.destination);
      graphRef.current = { ctx, analyser, source };
    } catch (e) {
      console.error(e);
    }
  }, [audioRef]);

  useEffect(() => {
    if (!isPlaying) {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      setWaveformData([]);
      prevBarsRef.current = Array.from({ length: BAR_COUNT }, () => 0);
      return;
    }

    const graph = graphRef.current;
    if (graph === null) {
      return;
    }

    const bufferLength = graph.analyser.frequencyBinCount;
    const frequencyData = new Uint8Array(bufferLength);
    const binWidth = graph.ctx.sampleRate / FFT_SIZE;

    const animate = () => {
      graph.analyser.getByteFrequencyData(frequencyData);
      // Bucket FFT bins into human-readable bands (see generateFrequencyBands).
      const bars = frequencyBands.map(({ low, high }) => {
        const startBin = Math.floor(low / binWidth);
        const endBin = Math.min(Math.ceil(high / binWidth), bufferLength - 1);
        let sum = 0;
        let count = 0;
        for (let j = startBin; j <= endBin; j++) {
          sum += frequencyData[j];
          count++;
        }
        return Math.pow((count > 0 ? sum / count : 0) / 255, 1.8);
      });
      // Asymmetric smoothing: bars rise quickly, fall slowly (feels more natural).
      const interpolated = bars.map((target, i) => {
        const prev = prevBarsRef.current[i];
        const factor = target > prev ? 0.75 : 0.4;
        return prev + (target - prev) * factor;
      });
      prevBarsRef.current = interpolated;
      setWaveformData(interpolated);
      frameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [isPlaying]);

  useEffect(
    () => () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      // Unhook the media element and release the context when the showcase unmounts.
      graphRef.current?.source.disconnect();
      graphRef.current?.ctx.close();
    },
    []
  );

  return { waveformData, ensureAudioGraph };
};
